/**
 * O modal "Importar", que escolhe entre Folha (PDF), Guia DAE (PDF) e Boleto (PDF).
 *
 * Papel: é o único botão de importação da aba Lançamentos. Folha e Guia DAE
 * chamam os manipuladores que já existiam — só mudou quem os dispara. Boleto
 * abre o seletor de PDFs (vários de uma vez), tenta abrir cada um sem senha,
 * pede a senha de cada um protegido com o nome do arquivo, e envia o que leu à
 * `ConferenciaDeBoletos`.
 *
 * Como usar: quem monta passa o plano de contas, os lançamentos carregados na
 * aba (para a duplicidade) e os dois manipuladores de Folha e Guia, e desmonta o
 * modal em `onFechar`. Fechar o modal, em qualquer etapa, descarta as senhas
 * digitadas e os PDFs lidos, porque nada disso sai do estado deste componente.
 * Um PDF que não abre, ou que não tem texto (escaneado), vira um aviso com o nome
 * dele, e não derruba os outros arquivos da mesma escolha.
 */
import { type Categoria, type LancamentoDaListagem } from '@concierge/shared';
import { useRef, useState } from 'react';

import { ErroDeSenhaDoPdf, extrairTextoDoPdf } from '@/shared/lib/pdf';
import { lerBoleto } from '../importacao/boleto-pdf';
import {
  type BoletoParaConferencia,
  montarLancamentoDeBoleto,
} from '../importacao/provedores-de-boleto';
import { ConferenciaDeBoletos, type ItemDoBoleto } from './conferencia-de-boletos';
import { AvisoDeConferencia } from './kit/aviso-de-conferencia';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { PedidoDeSenhaDoPdf } from './pedido-de-senha-do-pdf';

interface ModalDeImportacaoProps {
  /** O plano de contas, para a conferência do boleto. */
  plano: readonly Categoria[];
  /** Os lançamentos carregados na aba, para o aviso de duplicidade. */
  lancamentos: readonly LancamentoDaListagem[];
  /** "Folha (PDF)": o mesmo manipulador que abria a seleção da folha. */
  onFolha: () => void;
  /** "Guia DAE (PDF)": o mesmo manipulador que abria a seleção da guia. */
  onGuia: () => void;
  onFechar: () => void;
}

/** A etapa do boleto: a escolha de tipo, a senha de um arquivo, ou a conferência. */
type Etapa = 'escolha' | 'senha' | 'conferencia';

/** Um arquivo protegido que espera a senha, com o motivo da última tentativa. */
interface PedidoDeSenha {
  id: string;
  arquivo: File;
  motivo: 'necessaria' | 'incorreta';
}

/** O que o boleto guarda enquanto o modal está aberto. */
interface EstadoDaImportacao {
  etapa: Etapa;
  /** Verdadeiro enquanto um arquivo está sendo aberto. */
  ocupado: boolean;
  fila: PedidoDeSenha[];
  lidos: ItemDoBoleto[];
  avisos: string[];
}

/** O resultado de abrir um PDF de boleto, com ou sem senha. */
type Abertura =
  | { tipo: 'lido'; boleto: BoletoParaConferencia }
  | { tipo: 'senha'; motivo: 'necessaria' | 'incorreta' }
  | { tipo: 'sem-texto' }
  | { tipo: 'falha' };

/** A sequência dos identificadores dos arquivos do modal. */
let sequencia = 0;

/**
 * Um identificador novo para um arquivo da importação.
 *
 * @returns Um texto único nesta sessão da página.
 */
function novoId(): string {
  sequencia += 1;
  return String(sequencia);
}

/**
 * O aviso de um arquivo que não virou boleto.
 *
 * @param nome - O nome do arquivo.
 * @returns A frase que cita o arquivo, sem nenhum dado do conteúdo.
 */
function avisoDeFalha(nome: string): string {
  return `${nome}: não foi possível ler este PDF.`;
}

/**
 * Abre um PDF de boleto e lê o que ele tem de texto.
 *
 * Quem chama é a escolha de arquivos, sem senha, e o pedido de senha, com a
 * senha que a pessoa digitou. A senha só segue para `extrairTextoDoPdf`, e a
 * falha de leitura nunca sai daqui como mensagem.
 *
 * @param arquivo - O PDF escolhido.
 * @param senha - A senha, quando o arquivo já foi perguntado.
 * @returns O boleto lido; o motivo quando falta ou não serve a senha; ou a falha.
 */
async function abrirBoleto(arquivo: File, senha?: string): Promise<Abertura> {
  let texto: string;
  try {
    texto = await extrairTextoDoPdf(arquivo, senha);
  } catch (erro) {
    if (erro instanceof ErroDeSenhaDoPdf) return { tipo: 'senha', motivo: erro.motivo };
    return { tipo: 'falha' };
  }
  if (texto.trim() === '') return { tipo: 'sem-texto' };
  const linhas = texto.split('\n');
  return { tipo: 'lido', boleto: montarLancamentoDeBoleto(lerBoleto(linhas), linhas) };
}

/**
 * Decide a etapa pelo que sobrou: senha pendente, boletos lidos, ou nada.
 *
 * @param estado - O estado depois da última mudança.
 * @returns O mesmo estado com a etapa certa.
 */
function comEtapa(estado: EstadoDaImportacao): EstadoDaImportacao {
  if (estado.fila.length > 0) return { ...estado, etapa: 'senha' };
  if (estado.lidos.length > 0) return { ...estado, etapa: 'conferencia' };
  return { ...estado, etapa: 'escolha' };
}

/**
 * O modal "Importar".
 *
 * @param plano - O plano de contas.
 * @param lancamentos - Os lançamentos carregados na aba.
 * @param onFolha - Dispara a seleção do PDF da folha.
 * @param onGuia - Dispara a seleção do PDF da guia DAE.
 * @param onFechar - Desmonta o modal.
 * @returns A caixa de 460 px com as opções; a conferência do boleto, quando há boleto lido.
 */
export function ModalDeImportacao({
  plano,
  lancamentos,
  onFolha,
  onGuia,
  onFechar,
}: ModalDeImportacaoProps) {
  const [estado, setEstado] = useState<EstadoDaImportacao>({
    etapa: 'escolha',
    ocupado: false,
    fila: [],
    lidos: [],
    avisos: [],
  });
  const seletorDeBoletos = useRef<HTMLInputElement>(null);

  /**
   * Lê os PDFs escolhidos: os que abrem sem senha vão para a conferência, os
   * protegidos entram na fila de senha, e os que falham viram aviso.
   *
   * @param arquivos - Os PDFs escolhidos no seletor.
   */
  async function handleArquivos(arquivos: readonly File[]) {
    if (arquivos.length === 0) return;
    setEstado((anterior) => ({ ...anterior, ocupado: true }));
    const lidos: ItemDoBoleto[] = [];
    const fila: PedidoDeSenha[] = [];
    const avisos: string[] = [];
    for (const arquivo of arquivos) {
      const abertura = await abrirBoleto(arquivo);
      if (abertura.tipo === 'lido') {
        lidos.push({ id: novoId(), arquivo: arquivo.name, boleto: abertura.boleto });
      } else if (abertura.tipo === 'senha') {
        fila.push({ id: novoId(), arquivo, motivo: abertura.motivo });
      } else {
        avisos.push(avisoDeFalha(arquivo.name));
      }
    }
    setEstado((anterior) =>
      comEtapa({
        ...anterior,
        ocupado: false,
        fila: [...anterior.fila, ...fila],
        lidos: [...anterior.lidos, ...lidos],
        avisos: [...anterior.avisos, ...avisos],
      }),
    );
  }

  /**
   * Tenta abrir o arquivo que está na frente da fila, com a senha digitada.
   *
   * Uma senha que não serve mantém o arquivo na fila com o motivo `incorreta`,
   * e a pergunta volta. Um arquivo que abre sai da fila para a conferência; um que
   * falha sai com aviso.
   *
   * @param pedido - O arquivo que pediu a senha.
   * @param senha - A senha digitada.
   */
  async function handleAbrirComSenha(pedido: PedidoDeSenha, senha: string) {
    if (estado.ocupado) return;
    setEstado((anterior) => ({ ...anterior, ocupado: true }));
    const abertura = await abrirBoleto(pedido.arquivo, senha);
    setEstado((anterior) => {
      if (abertura.tipo === 'senha') {
        const fila = anterior.fila.map((item) =>
          item.id === pedido.id ? { ...item, motivo: abertura.motivo } : item,
        );
        return comEtapa({ ...anterior, ocupado: false, fila });
      }
      const fila = anterior.fila.filter((item) => item.id !== pedido.id);
      if (abertura.tipo === 'lido') {
        const lido: ItemDoBoleto = {
          id: pedido.id,
          arquivo: pedido.arquivo.name,
          boleto: abertura.boleto,
        };
        return comEtapa({
          ...anterior,
          ocupado: false,
          fila,
          lidos: [...anterior.lidos, lido],
        });
      }
      return comEtapa({
        ...anterior,
        ocupado: false,
        fila,
        avisos: [...anterior.avisos, avisoDeFalha(pedido.arquivo.name)],
      });
    });
  }

  /**
   * "Pular": descarta só o arquivo que pediu a senha, e segue com o próximo.
   *
   * @param id - O identificador do arquivo pulado.
   */
  function handlePular(id: string) {
    setEstado((anterior) =>
      comEtapa({ ...anterior, fila: anterior.fila.filter((item) => item.id !== id) }),
    );
  }

  /**
   * "Boleto (PDF)": abre o seletor de PDFs, de vários de uma vez.
   *
   * O clique precisa acontecer dentro do gesto do usuário, por isso o seletor
   * está no corpo do modal e é disparado aqui mesmo.
   */
  function handleEscolherBoleto() {
    seletorDeBoletos.current?.click();
  }

  if (estado.etapa === 'conferencia') {
    return (
      <ConferenciaDeBoletos
        itens={estado.lidos}
        avisosDeArquivo={estado.avisos}
        plano={plano}
        lancamentos={lancamentos}
        onFechar={onFechar}
      />
    );
  }

  const pedido = estado.fila[0];

  return (
    <CaixaDeDialogo
      titulo="Importar"
      largura={460}
      onFechar={onFechar}
      rodape={
        <Botao variante="fantasma" onClick={onFechar}>
          Cancelar
        </Botao>
      }
    >
      {estado.avisos.map((aviso) => (
        <AvisoDeConferencia key={aviso}>{aviso}</AvisoDeConferencia>
      ))}
      {estado.etapa === 'senha' && pedido !== undefined ? (
        <PedidoDeSenhaDoPdf
          key={pedido.id}
          nomeDoArquivo={pedido.arquivo.name}
          motivo={pedido.motivo}
          ocupado={estado.ocupado}
          onAbrir={(senha) => void handleAbrirComSenha(pedido, senha)}
          onPular={() => handlePular(pedido.id)}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <Botao
            variante="fantasma"
            disabled={estado.ocupado}
            onClick={() => {
              onFolha();
              onFechar();
            }}
          >
            Folha (PDF)
          </Botao>
          <Botao
            variante="fantasma"
            disabled={estado.ocupado}
            onClick={() => {
              onGuia();
              onFechar();
            }}
          >
            Guia DAE (PDF)
          </Botao>
          <Botao variante="fantasma" disabled={estado.ocupado} onClick={handleEscolherBoleto}>
            Boleto (PDF)
          </Botao>
          <p className="text-[11px] text-texto-fraco">
            {estado.ocupado
              ? 'Lendo os PDFs…'
              : 'Folha e Guia DAE seguem como antes. Boleto lê o PDF no navegador e pede a senha de cada arquivo protegido; o arquivo e a senha não são guardados.'}
          </p>
          <input
            ref={seletorDeBoletos}
            type="file"
            accept="application/pdf"
            multiple
            aria-label="PDF do boleto"
            className="hidden"
            onChange={(evento) => {
              const arquivos = Array.from(evento.target.files ?? []);
              evento.target.value = '';
              void handleArquivos(arquivos);
            }}
          />
        </div>
      )}
    </CaixaDeDialogo>
  );
}
